import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  TextProps,
  TypographyProps,
} from '@chakra-ui/react'
import React, { ReactElement, useEffect, useState } from 'react'

import { IconByName, IconNamesBrand } from '~shared-components/icons/brand'

import { StandardText } from '../Typography/Typography'

const BEZEL = 3
interface ActiveButtonProps extends BoxProps {
  basisArray: number[]
  current: number
  vertical?: boolean
}

const ActiveButton: React.FC<ActiveButtonProps> = ({
  basisArray,
  current,
  vertical: v,
  ...props
}) => {
  let accumulatedBasis = 0
  for (let i = 0; i < current; i++) {
    accumulatedBasis += basisArray[i]
  }

  const currentBasis = basisArray[current]
  const dim100 = `calc(100% - ${2 * BEZEL}px)`
  const dimCurrent = `calc(${currentBasis}% - ${2 * BEZEL}px)`
  const position = `calc(${accumulatedBasis}% + ${BEZEL}px)`

  return (
    <Box
      className="sliding-active-button"
      h={v ? dimCurrent : dim100}
      left={v ? undefined : position}
      position="absolute"
      top={v ? position : undefined}
      transition="all 0.5s ease-in-out"
      w={v ? dim100 : dimCurrent}
      {...props}
    />
  )
}

interface SlidingButtonWrapperProps extends BoxProps {
  basis?: number
}

const sxSlidingButtonWrapper = {
  '& > *': {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    px: 4,
    py: 2,
  },
  '&.active': {
    '.chakra-icon': {
      fill: 'white.0',
    },
    color: 'white.0',
  },
  '.chakra-icon': {
    fill: 'neutral.90',
    left: '-5px',
    position: 'relative',
    transition: 'fill 0.5s ease-in-out',
  },
}
const SlidingButtonWrapper: React.FC<SlidingButtonWrapperProps> = ({
  basis = 50,
  ...props
}) => (
  <Box
    color="neutral.90"
    flexBasis={`${basis}%`}
    sx={sxSlidingButtonWrapper}
    textAlign="center"
    transition="color 0.5s ease-in-out"
    userSelect="none"
    {...props}
  />
)

interface ContainerProps extends FlexProps {
  hasBorder?: boolean
  radius: number | undefined
  vertical?: boolean
}

const cssContainer = {
  '& > *:not(.sliding-active-button)': {
    zIndex: 1,
  },
}

const Container: React.FC<ContainerProps> = ({
  bgColor,
  hasBorder,
  radius,
  vertical,
  ...props
}) => {
  const sxContainer = {
    '.sliding-active-button': {
      background: bgColor,
      borderRadius: radius ? `${radius - 1}px` : undefined,
    },
  }
  return (
    <Flex
      alignItems="center"
      background="neutral.10"
      border={hasBorder ? '1px solid ' : undefined}
      borderColor="neutral.20"
      borderRadius={radius ? `${radius}px` : undefined}
      css={cssContainer}
      flexDirection={vertical ? 'column' : 'row'}
      flexGrow={1}
      justifyContent="center"
      position="relative"
      sx={sxContainer}
      {...props}
    />
  )
}

const ClickableButton: React.FC<FlexProps> = props => (
  <Flex flexDirection="row" justifyContent="center" zIndex={1} {...props} />
)

const ButtonText: React.FC<TextProps & TypographyProps> = props => (
  <StandardText as="span" color="inherit" whiteSpace="normal" {...props} />
)
interface SlidingButtonProps {
  iconName?: IconNamesBrand
  onClick?: () => void
  span?: number
}
export const SlidingButton: React.FC<SlidingButtonProps> = ({
  children,
  iconName,
  onClick,
}) => (
  <ClickableButton onClick={onClick}>
    {iconName && <IconByName boxSize="16px" name={iconName} />}
    <ButtonText>{children}</ButtonText>
  </ClickableButton>
)

export interface SlidingButtonsProps extends FlexProps {
  activeButton?: number
  hasBorder?: boolean
  initialActiveButton?: number
  radius?: 16 | 3 | 0
  vertical?: boolean
}

export const SlidingButtons: React.FC<SlidingButtonsProps> = ({
  activeButton,
  children,
  color = 'neutral.70',
  hasBorder,
  initialActiveButton = 0,
  radius = 0,
  vertical,
  ...props
}) => {
  const [activeButtonIndex, setActiveButtonIndex] = useState(
    initialActiveButton,
  )

  useEffect(() => {
    if (activeButton !== undefined) setActiveButtonIndex(activeButton)
  }, [activeButton])

  const spans =
    React.Children.map(
      children,
      child => (child as ReactElement).props.span || 1,
    ) ?? []
  const totalButtons =
    React.Children.count(children) +
    spans.reduce((acc, cur) => acc + cur - 1, 0)
  const basisArray = spans.map(s => Math.round((s * 100) / totalButtons))

  return (
    <Container
      bgColor={color}
      hasBorder={hasBorder}
      radius={radius}
      vertical={vertical}
      {...props}
    >
      {children && (
        <>
          {React.Children.map(children, (child, index) => {
            return (
              <SlidingButtonWrapper
                key={index}
                basis={basisArray[index]}
                className={index === activeButtonIndex ? 'active' : ''}
                onClick={() => setActiveButtonIndex(index)}
              >
                {child}
              </SlidingButtonWrapper>
            )
          })}
          <ActiveButton
            basisArray={basisArray}
            current={activeButtonIndex}
            vertical={vertical}
          />
        </>
      )}
    </Container>
  )
}
